import { Bookmark } from "lucide-react";

export const SYNASTRY_SECTIONS = [
  ["emotional", "How You Share Feelings"],
  ["attraction", "What Draws You Together"],
  ["meaning", "How You Make Meaning"],
  ["friction", "Where Sparks Need Structure"],
  ["composite", "The Third Personality"],
  ["karmic", "Your Shared Lessons"],
];

const ZODIAC = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];
const ABBR = { Sun: "Su", Moon: "Mo", Mercury: "Me", Venus: "Ve", Mars: "Ma", Jupiter: "Ju", Saturn: "Sa", Uranus: "Ur", Neptune: "Ne", Pluto: "Pl" };

const lonOf = (sign, deg) => ZODIAC.indexOf(sign) * 30 + deg;
const mk = (pairs) => Object.fromEntries(pairs.map(([p, s, d]) => [p, { sign: s, deg: d, lon: lonOf(s, d) }]));

export const SAMPLE_SYNASTRY = {
  a: { name: "Alex", chart: mk([["Sun", "Leo", 12], ["Moon", "Cancer", 4], ["Mercury", "Virgo", 2], ["Venus", "Cancer", 21], ["Mars", "Libra", 9], ["Jupiter", "Sagittarius", 15], ["Saturn", "Capricorn", 26], ["Uranus", "Capricorn", 8], ["Neptune", "Capricorn", 14], ["Pluto", "Scorpio", 18]]) },
  b: { name: "Jordan", chart: mk([["Sun", "Taurus", 27], ["Moon", "Capricorn", 2], ["Mercury", "Gemini", 11], ["Venus", "Cancer", 25], ["Mars", "Aries", 6], ["Jupiter", "Cancer", 3], ["Saturn", "Aquarius", 20], ["Uranus", "Aquarius", 1], ["Neptune", "Capricorn", 29], ["Pluto", "Sagittarius", 5]]) },
  analysis: {
    score: 82, archetype: "The Safe Harbour", tagline: "Two hearts that understand without words",
    forces: [{ name: "Emotional Attunement", value: 91 }, { name: "Magnetic Pull", value: 84 }, { name: "Lasting Commitment", value: 72 }],
    aspects: [{ a: "Venus", b: "Venus", aspect: "conjunction", orb: 1.2 }, { a: "Moon", b: "Moon", aspect: "opposition", orb: 2.0 }, { a: "Mars", b: "Mars", aspect: "opposition", orb: 3.1 }],
  },
  text: {
    summary: "Alex's Venus sits right beside Jordan's Venus, so you love in the same language. Your Moons face each other across the zodiac: one of you nurtures, the other protects.",
    emotional: "Alex feels first and talks later; Jordan needs to understand a feeling before sharing it. Your opposing Moons make this a natural balance rather than a clash.\n\nWhen one of you is overwhelmed, the other instinctively steadies the boat.",
    attraction: "With both Venuses in Cancer, you're drawn to care, loyalty and the comfort of home. Mars opposite Mars adds spark: playful rivalry keeps the attraction alive.",
    meaning: "Alex's Jupiter trines Jordan's Sun, so you widen each other's world.",
    friction: "Saturn squares can make plans feel slow. Treat them as structure, not walls.",
    composite: "Together you become a calm, protective unit that others lean on.",
    karmic: "Alex learns patience from Jordan; Jordan learns to trust feelings from Alex.",
  },
};

function Dial({ score, size = 132 }) {
  const r = size / 2 - 10, c = Math.PI * 2 * r, arc = c * 0.75;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="synastry-score-dial" className="mx-auto">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#232838" strokeWidth="8" strokeDasharray={`${arc} ${c}`} strokeLinecap="round" transform={`rotate(135 ${size / 2} ${size / 2})`} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#D4AF37" strokeWidth="8" strokeDasharray={`${(arc * score) / 100} ${c}`} strokeLinecap="round" transform={`rotate(135 ${size / 2} ${size / 2})`} />
      <text x="50%" y="50%" textAnchor="middle" dy="8" fill="#F7F4EE" fontSize={size / 3.6} fontFamily="Cormorant Garamond, serif">{score}</text>
      <text x="50%" y="50%" textAnchor="middle" dy={size / 4.2} fill="#D4AF37" fontSize="9" letterSpacing="2">SCORE</text>
    </svg>
  );
}

export function Wheel({ chart, name, size = 150 }) {
  const cx = size / 2, R = size / 2 - 4, inner = R * 0.78;
  const pt = (lon, rad) => [cx + rad * Math.cos(Math.PI - (lon * Math.PI) / 180), cx - rad * Math.sin(Math.PI - (lon * Math.PI) / 180)];
  return (
    <figure className="text-center">
      <svg viewBox={`0 0 ${size} ${size}`} className="mx-auto w-full h-auto max-w-[170px]">
        <circle cx={cx} cy={cx} r={R} fill="none" stroke="#D4AF37" strokeOpacity="0.7" />
        <circle cx={cx} cy={cx} r={inner} fill="none" stroke="#D4AF37" strokeOpacity="0.4" />
        {ZODIAC.map((s, i) => {
          const [x1, y1] = pt(i * 30, inner), [x2, y2] = pt(i * 30, R), [tx, ty] = pt(i * 30 + 15, (R + inner) / 2);
          return (
            <g key={s}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#6b5a24" />
              <text x={tx} y={ty + 2.5} textAnchor="middle" fontSize="6.5" fill="#D4AF37">{s.slice(0, 3).toUpperCase()}</text>
            </g>
          );
        })}
        {Object.entries(chart).map(([p, v], j) => {
          const [x, y] = pt(v.lon, inner * (0.82 - (j % 3) * 0.16));
          return (
            <g key={p}>
              <circle cx={x} cy={y} r="2" fill="#D4AF37" />
              <text x={x} y={y - 4} textAnchor="middle" fontSize="6.5" fill="#F7F4EE">{ABBR[p]}</text>
            </g>
          );
        })}
      </svg>
      <figcaption className="text-sm text-[#D6D0C5] mt-1">{name}</figcaption>
    </figure>
  );
}

export function SynastrySnapshot({ report }) {
  const { a, b, analysis: an } = report;
  return (
    <div data-testid="synastry-snapshot" className="text-center">
      <p className="text-[11px] uppercase tracking-[0.3em] gold-text">Synastry reading</p>
      <h2 className="text-3xl font-light mt-1 mb-3" data-testid="synastry-names">{a.name} & {b.name}</h2>
      <Dial score={an.score} />
      <p className="text-2xl font-light mt-2" data-testid="synastry-archetype">{an.archetype}</p>
      <p className="text-sm italic text-[#A7B0C0] mb-5">“{an.tagline}”</p>
      <div className="space-y-3 text-left" data-testid="synastry-forces">
        {an.forces.map((f) => (
          <div key={f.name}>
            <div className="flex justify-between text-sm mb-1"><span className="text-[#E8DCC4]">{f.name}</span><span className="gold-text">{f.value}%</span></div>
            <div className="h-1.5 rounded-full bg-[#232838]"><div className="h-full rounded-full bg-[#D4AF37]" style={{ width: `${f.value}%` }} /></div>
          </div>
        ))}
      </div>
      {report.text?.summary && <p className="text-base text-[#D6D0C5] leading-relaxed mt-5 text-left" data-testid="synastry-summary">{report.text.summary}</p>}
    </div>
  );
}

export function SynastrySections({ report, limit }) {
  const list = limit ? SYNASTRY_SECTIONS.slice(0, limit) : SYNASTRY_SECTIONS;
  return (
    <div className="space-y-4" data-testid="synastry-sections">
      {list.map(([k, title]) => (
        <section key={k} data-testid={`synastry-section-${k}`} className="bg-[#0F1523]/90 border border-[#D4AF37]/20 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.25em] gold-text mb-1">The reading</p>
          <h3 className="text-xl font-light mb-2">{title}</h3>
          {(report.text?.[k] || "").split(/\n\s*\n/).map((p, i) => (
            <p key={i} className="text-base text-[#D6D0C5] leading-relaxed mb-2 last:mb-0">{p}</p>
          ))}
        </section>
      ))}
    </div>
  );
}

export function SynastryCharts({ report }) {
  return (
    <div className="bg-[#0F1523]/90 border border-[#D4AF37]/20 rounded-2xl p-5" data-testid="synastry-charts">
      <p className="text-[10px] uppercase tracking-[0.25em] gold-text mb-3">Two skies</p>
      <div className="grid grid-cols-2 gap-2">
        <Wheel chart={report.a.chart} name={report.a.name} />
        <Wheel chart={report.b.chart} name={report.b.name} />
      </div>
      <ul className="mt-4 space-y-1.5" data-testid="synastry-contacts">
        {report.analysis.aspects.slice(0, 6).map((x, i) => (
          <li key={i} className="text-sm text-[#D6D0C5] flex justify-between gap-2">
            <span>{report.a.name}'s {x.a} <span className="gold-text">{x.aspect}</span> {report.b.name}'s {x.b}</span>
            <span className="text-[#6c7686] shrink-0">{x.orb}°</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SampleScreen({ sample }) {
  return (
    <div className="p-5">
      <p className="text-[10px] uppercase tracking-[0.25em] gold-text">{sample.kicker}</p>
      <h3 className="text-2xl font-light mt-1 mb-3">{sample.title}</h3>
      {sample.lines.map((l) => <p key={l} className="text-sm text-[#D6D0C5] leading-relaxed mb-3">{l}</p>)}
      <p className="text-sm text-[#D6D0C5]/50 leading-relaxed blur-[2px] select-none">The reading continues with several more sections written for your chart, covering what this means for you and what to do next.</p>
      <p className="mt-4 flex items-center gap-1.5 text-sm gold-text"><Bookmark size={14} /> Save to archive</p>
    </div>
  );
}

export function PhoneFrame({ children, testId }) {
  return (
    <div data-testid={testId} className="relative w-[260px] h-[520px] shrink-0 rounded-[2.4rem] border-[6px] border-[#1c2130] bg-[#070A12] shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden">
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 rounded-full bg-black z-10" />
      <div className="pt-8 h-full overflow-hidden">{children}</div>
      <div className="absolute bottom-0 inset-x-0 h-20 bg-gradient-to-t from-[#070A12] to-transparent" />
    </div>
  );
}
